// Moving Booking's upcoming staffing into Hub shifts (ADR 0024). Booking
// retires its staffing screens; its upcoming shifts, the people on them and
// their assignments come over once (re-runnable, idempotent), and its past
// records stay in Booking, read-only.
//
//   GET <booking>/api/internal/hub/v1/staffing/upcoming   (Hub's read key)
//
// People are linked ONLY by their BRE Auth subject (Booking's
// staffing_people.portal_user_id). Email, phone and name are evidence for
// the person who reviews the people Hub couldn't link; they never link
// anyone. Pay rates, hours estimates, pay status and paid dates are not
// read: Hub's shifts record attendance, not pay.

import { z } from 'zod'

export const STAFFING_IMPORT_PATH = '/staffing/upcoming'
export const STAFFING_IMPORT_SOURCE = 'booking'

const Text = (max: number) => z.string().max(max)
const NullableText = (max: number) => z.string().max(max).nullable()
const LOCAL = z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/)
const KIND = z.enum(['volunteer', 'staff'])

// Booking's contract v1. Parsed with zod's default (unknown keys dropped),
// so anything else Booking might add -- a pay field included -- never
// reaches core.
export const SourceStaffingAssignment = z.object({
  sourceId: Text(200).min(1),
  personSourceId: Text(200).min(1),
  status: Text(60),
  active: z.boolean(),
  slotNumber: z.number().int().optional(),
})

export const SourceStaffingShift = z.object({
  sourceId: Text(200).min(1),
  title: Text(500),
  department: Text(200).nullable().optional(),
  roleType: Text(60).optional(),
  kind: KIND,
  paid: z.boolean(),
  status: Text(60).optional(),
  canceled: z.boolean(),
  openSignup: z.boolean().optional(),
  start: z.string(),
  end: z.string(),
  startLocal: LOCAL,
  endLocal: LOCAL,
  neededCount: z.number().int(),
  requiredCredentialTypes: z.array(Text(200)).max(50).default([]),
  notes: NullableText(4000).optional(),
  templateSourceId: NullableText(200).optional(),
  event: z.object({ type: z.enum(['rental', 'show', 'bre_event']), sourceId: Text(200).min(1), title: NullableText(500), url: NullableText(1000).optional() }).nullable(),
  assignments: z.array(SourceStaffingAssignment).max(500),
})

export const SourceStaffingPerson = z.object({
  sourceId: Text(200).min(1),
  sub: NullableText(200),
  firstName: Text(200),
  lastName: Text(200),
  personType: Text(60).optional(),
  kind: KIND,
  active: z.boolean(),
  approvalStatus: Text(60).optional(),
  evidence: z.object({ email: NullableText(320), phone: NullableText(60) }).partial().optional(),
})

export const SourceStaffingExport = z.object({
  version: z.literal(1),
  asOf: z.string(),
  from: z.string(),
  shifts: z.array(SourceStaffingShift).max(2000),
  people: z.array(SourceStaffingPerson).max(10000),
  truncated: z.boolean().optional(),
})
export type SourceStaffingExport = z.infer<typeof SourceStaffingExport>

// What the web Worker hands core: the export, the issuer the subjects
// belong to, and the environment Booking was read in.
export const StaffingImportApplyBody = z.object({ issuer: z.string().url().max(200), environment: z.enum(['production', 'development']), export: SourceStaffingExport })

export type StaffingImportCounts = {
  shiftsSeen: number
  shiftsCreated: number
  shiftsAlreadyImported: number
  shiftsSkippedCanceled: number
  shiftsNotImportable: number
  shiftsWithoutHubEvent: number
  assignmentsCreated: number
  assignmentsAlreadyImported: number
  assignmentsUnlinked: number
  peopleLinked: number
  hubPeopleCreated: number
  workforceRecordsCreated: number
  peopleUnlinked: number
}

export const emptyStaffingImportCounts = (): StaffingImportCounts => ({
  shiftsSeen: 0,
  shiftsCreated: 0,
  shiftsAlreadyImported: 0,
  shiftsSkippedCanceled: 0,
  shiftsNotImportable: 0,
  shiftsWithoutHubEvent: 0,
  assignmentsCreated: 0,
  assignmentsAlreadyImported: 0,
  assignmentsUnlinked: 0,
  peopleLinked: 0,
  hubPeopleCreated: 0,
  workforceRecordsCreated: 0,
  peopleUnlinked: 0,
})

export type StaffingImportRun = {
  id: string
  environment: string
  startedAt: string
  finishedAt: string | null
  status: 'ok' | 'error'
  counts: StaffingImportCounts
  error: string | null
  sourceAsOf: string | null
  runByName: string | null
}

export const STAFFING_REVIEW_KINDS = ['person', 'assignment', 'shift'] as const
export type StaffingReviewKind = (typeof STAFFING_REVIEW_KINDS)[number]

// One thing the import couldn't bring over by itself, for a person to handle.
export type StaffingReviewItem = {
  id: string
  kind: StaffingReviewKind
  sourceId: string
  // Booking's name for the person or shift, and the contact details Booking
  // had: evidence for the reviewer, never used to link.
  label: string
  detail: string | null
  evidence: { email: string | null; phone: string | null } | null
  reason: string
  shiftId: string | null
  firstSeenAt: string
  lastSeenAt: string
  resolvedAt: string | null
  resolvedByName: string | null
  resolutionNote: string | null
}

export const StaffingReviewResolveBody = z.object({ note: z.string().trim().min(3).max(500) })

export const STAFFING_IMPORT_UNLINKED_REASON = 'Booking has no BRE account for this person. Add them to the shift by hand.'
